import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { userAvatar, teacherAvatar } from '../assets/teacherAvatar';
import { sendMessage as sendMessageToAPI, isApiAvailable, getApiStatusMessage } from '../services/openaiService';
import { speak, getVoices, cleanTranscript, initializeSpeechSynthesis, isMobileSafari } from '../services/speechService';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { DEFAULT_CHAT_ROLE } from '../data/chatRoles';
import { OPENING_GREETINGS } from '../data/openingGreetings';

// Create context
const ChatContext = createContext();

// Custom hook to use chat context
export const useChat = () => useContext(ChatContext);

// Provider component
export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [pendingResponse, setPendingResponse] = useState(null); // For mobile Safari manual play
  const [chatRole, setChatRoleState] = useState(DEFAULT_CHAT_ROLE);
  
  const conversationHistoryRef = useRef([]);
  const chatRoleRef = useRef(DEFAULT_CHAT_ROLE);
  const messageIdRef = useRef(1);

  // Speech recognition hook from react-speech-recognition
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  // Initialize voices
  useEffect(() => {
    const loadVoices = async () => {
      const availableVoices = await getVoices();
      setVoices(availableVoices);
      
      // Try to find a Chinese voice
      const chineseVoice = availableVoices.find(voice => voice.lang.includes('zh'));
      if (chineseVoice) {
        setSelectedVoice(chineseVoice);
      } else {
        setSelectedVoice(availableVoices[0]);
      }
    };
    
    loadVoices();
  }, []);

  // Check browser support for speech recognition
  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      setError('Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.');
    }
  }, [browserSupportsSpeechRecognition]);

  // Speak a message using the selected voice
  const speakMessage = useCallback(async (text) => {
    if (!text) return;
    
    setIsSpeaking(true);
    try {
      await speak(text, 1, 1, selectedVoice);
      // Clear pending response if speech was successful
      setPendingResponse(null);
    } catch (error) {
      // On mobile Safari, if speech fails, store the text for manual play
      if (isMobileSafari()) {
        setPendingResponse(text);
      }
    } finally {
      setIsSpeaking(false);
    }
  }, [selectedVoice]);

  // Manual play function for mobile Safari
  const playPendingResponse = useCallback(() => {
    if (pendingResponse) {
      speakMessage(pendingResponse);
    }
  }, [pendingResponse, speakMessage]);

  const placeOpeningGreeting = useCallback(() => {
    const greeting = OPENING_GREETINGS[Math.floor(Math.random() * OPENING_GREETINGS.length)];
    const initialMessage = {
      id: messageIdRef.current++,
      content: greeting,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isUser: false,
      status: 'read'
    };

    setMessages([initialMessage]);
    conversationHistoryRef.current = [{ role: 'assistant', content: greeting }];
    setConversationStarted(true);
    setIsLoading(false);
    setTimeout(() => speakMessage(greeting), 0);
  }, [speakMessage]);

  // Show a local greeting. Do not call the chat API.
  const startConversation = useCallback(() => {
    if (!isInitialized) {
      setError('Chat system is not initialized. Please refresh the page.');
      return;
    }

    setError(null);
    placeOpeningGreeting();
  }, [isInitialized, placeOpeningGreeting]);

  const setChatRole = useCallback((role) => {
    const nextRole = role || DEFAULT_CHAT_ROLE;
    const roleChanged = nextRole !== chatRoleRef.current;
    chatRoleRef.current = nextRole;
    setChatRoleState(nextRole);
    // Backend contract: switching LARP clears history and starts a fresh scene.
    if (roleChanged) {
      placeOpeningGreeting();
    }
  }, [placeOpeningGreeting]);

  // Initialize the chat system
  useEffect(() => {
    const initAPI = async () => {
      try {
        // No longer need to initialize OpenAI client - using backend API
        setIsInitialized(true);
        setError(null);
        
        if (isApiAvailable()) {
          console.log('✅ Production mode: Backend API will handle OpenAI requests');
        } else {
          console.warn('⚠️ Development mode: Using fallback responses. Set up backend API for real AI.');
        }
      } catch (error) {
        console.error('Error initializing chat system:', error);
        setError('Failed to initialize chat system. Please refresh the page.');
        setIsInitialized(false);
      }
    };
    
    initAPI();
  }, []);

  // Add a message to the UI
  const addMessage = (content, isUser) => {
    const newMessage = {
      id: messageIdRef.current++,
      content,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isUser,
      status: 'sent'
    };
    
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  };

  // Update message status
  const updateMessageStatus = (id, status) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === id ? { ...msg, status } : msg
      )
    );
  };

  // Start recording user's voice
  const startVoiceRecording = () => {
    if (!browserSupportsSpeechRecognition) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }
    
    if (!isMicrophoneAvailable) {
      setError('Microphone access is required. Please allow microphone permissions.');
      return;
    }
    
    if (listening) return;

    // Initialize speech synthesis on user gesture (for mobile Safari)
    initializeSpeechSynthesis();
    
    resetTranscript();
    SpeechRecognition.startListening({ 
      continuous: true,
      language: 'zh-CN' // Set to Chinese for this app
    });
  };

  // Stop recording user's voice
  const stopVoiceRecording = () => {
    if (!listening) return;
    
    SpeechRecognition.stopListening();
    
    // Process the transcript after stopping
    if (transcript.trim()) {
      const cleanedTranscript = cleanTranscript(transcript);
      
      // Check if this message would be a duplicate of the last message
      const lastMessage = messages[messages.length - 1];
      if (!(lastMessage && lastMessage.isUser && lastMessage.content === cleanedTranscript)) {
        sendMessage(cleanedTranscript);
      }
    }
  };

  // Process a text response from OpenAI
  const processOpenAIResponse = async (userMessageId, role) => {
    try {
      // Mark the user message as delivered
      updateMessageStatus(userMessageId, 'delivered');
      
      const turns = conversationHistoryRef.current.map((turn) => ({
        role: turn.role,
        content: turn.content,
      }));
      const apiResponse = await sendMessageToAPI(turns, { role });
      const response = apiResponse.choices[0].message.content;
      
      // Mark the user message as read
      updateMessageStatus(userMessageId, 'read');
      
      // Add the assistant's message to UI
      addMessage(response, false);
      
      // Add the assistant's response to the conversation history
      conversationHistoryRef.current.push({
        role: "assistant",
        content: response
      });
      
      // Speak the response
      speakMessage(response);
      
    } catch (error) {
      console.error('Error processing OpenAI response:', error);
      setError('Failed to get response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Send a message to the chat
  const sendMessage = async (content) => {
    if (!content?.trim()) return;
    if (!isInitialized) {
      setError('Chat system is not initialized. Please refresh the page.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    // Add user message to UI
    const userMessage = addMessage(content, true);
    
    // Add the user's message to the conversation history
    conversationHistoryRef.current.push({
      role: "user",
      content
    });
    
    const role = chatRoleRef.current || DEFAULT_CHAT_ROLE;
    processOpenAIResponse(userMessage.id, role);
  };



  // Change the selected voice
  const changeVoice = (voice) => {
    setSelectedVoice(voice);
  };

  // Reset the conversation
  const resetConversation = () => {
    resetTranscript();
    setPendingResponse(null);
    setError(null);
    placeOpeningGreeting();
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        sendMessage,
        isLoading,
        error,
        userAvatar,
        teacherAvatar,
        isInitialized,
        isSpeaking,
        isRecording: listening,
        voices,
        selectedVoice,
        changeVoice,
        startVoiceRecording,
        stopVoiceRecording,
        hasApiKey: isApiAvailable(), // True if backend API is available
        resetConversation,
        startConversation,
        conversationStarted,
        // Speech recognition status
        transcript,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
        // Mobile Safari TTS support
        pendingResponse,
        playPendingResponse,
        isMobileSafari: isMobileSafari(),
        // API status for UI
        apiStatusMessage: getApiStatusMessage(),
        chatRole,
        setChatRole
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext; 